"use client";

import Link from "next/link";
import {
  RoundedImage,
  Separator,
  ScrollContainer,
  Tag,
  Button,
} from "@/components";
import { MdAccessTime } from "react-icons/md";
import clsx from "clsx";
import { formatCurrency, timeAgo } from "@/lib/utils";

export const perks: Perk[] = [
  {
    image: null,
    category: "Technology",
    title: "Full Stack Bootcamp",
    name: "Full Stack Developer",
    institution: "Codecademy",
    duration: "3 months",
    description:
      "Learn HTML, CSS, JavaScript, React and Node.js through hands-on projects and challenges.",
    createdAt: "2025-06-15T10:00:00Z",
    cost: 49.99,
    previousCost: 99.99,
    discount: 50,
  },
  {
    image: null,
    category: "Design",
    title: "UX/UI Design Basics",
    name: "UX Designer",
    institution: "Coursera",
    duration: "6 weeks",
    description:
      "Understand the fundamentals of user experience and interface design to build intuitive apps and websites.",
    createdAt: "2025-07-01T12:00:00Z",
    cost: 29.99,
    previousCost: 59.99,
    discount: 50,
  },
  {
    image: null,
    category: "AI & Data",
    title: "Intro to AI",
    name: "AI Fundamentals",
    institution: "Google",
    duration: "2 months",
    description:
      "Learn the core concepts behind AI including machine learning, neural networks, and real-world applications.",
    createdAt: "2025-06-28T08:30:00Z",
    cost: 0,
    previousCost: 0,
    discount: 0,
  },
  {
    image: null,
    category: "Business",
    title: "Startup Business Essentials",
    name: "Startup Incubator",
    institution: "LinkedIn Learning",
    duration: "4 weeks",
    description:
      "Master the fundamentals of starting a business, from idea validation to scaling and funding.",
    createdAt: "2025-07-05T16:45:00Z",
    cost: 19.99,
    previousCost: 39.99,
    discount: 50,
  },
];

export interface Perk {
  image: string | null;
  category: string;
  title: string;
  name: string;
  institution: string;
  duration: string;
  description: string;
  createdAt: string; //ISO
  cost?: number;
  previousCost?: number;
  discount?: number;
}

interface PerkProps {
  perk: Perk;
  className?: string;
  buttonText?: string;
  redirectOnPress?: string | null;
}

export const PerkItem = ({
  perk,
  className,
  buttonText = "Get Perk",
  redirectOnPress,
}: PerkProps) => {
  const button = (
    <Button className="rounded-[7px]! w-[115px] h-[29px] px-0! text-[12px]! font-[500]! py-0 leading-none!">
      {buttonText}
    </Button>
  );

  return (
    <div
      className={clsx(
        `
          w-[360px] h-[288px]
          flex-shrink-0 
          rounded-[10px] bg-white 
          shadow-[0px_0px_8px_0px_rgba(0,0,0,0.15)]
          pt-[12px] pb-[40px] pr-[10px] pl-[11px]
        `,
        className
      )}
    >
      <div className="pl-[15px] pr-[5px]">
        {/* Tag */}
        <div className="flex flex-row justify-end">
          <Tag value={perk.category} />
        </div>
        {/* Image and Name */}
        <div className="flex flex-row pb-[12.5px]">
          <RoundedImage
            src={perk.image ?? undefined}
            className="aspect[1] w-[35px] mr-[11px]"
          />
          <div className="flex flex-col">
            <p className="text-[14px] font-[600] line-clamp-1">{perk.title}</p>
            <p className="text-[12px] font-[500] text-[#8A8A8A] mt-[-3px]">
              Institution: {perk.institution}
            </p>
          </div>
        </div>
        {/* Tags */}
        <div className="flex flex-row gap-[11px] pb-[12px]">
          <Tag value={perk.name} />
          <p className="text-[14px] text-[#8A8A8A] font-[600]">
            Duration: <span className="font-[400]">{perk.duration}</span>
          </p>
        </div>
        {/* Description */}
        <ScrollContainer height={36}>
          <p className="text-[12px] text-[#8A8A8A] font-[400] pr-[11px]">
            {perk.description}
          </p>
        </ScrollContainer>
        <div className="pt-[15px] flex flex-row space-x-[5.5px] pb-[13px]">
          <MdAccessTime className="w-[15px] aspect-[1] text-[#8A8A8A]" />
          <p className="text-[#8A8A8A] text-[12px] font-[600]">
            Starts:{" "}
            <span className="font-[400]">{timeAgo(perk.createdAt)}</span>
          </p>
        </div>
      </div>
      <Separator />
      <div className="px-[15px] pt-[18px] flex flex-row justify-between h-[50px] items-center">
        <div className="flex flex-col gap-[6px]">
          <div className="flex flex-row items-center">
            {perk.previousCost && perk.previousCost !== 0 ? (
              <p className="text-[14px] font-[600] line-through pr-[7px]">
                {formatCurrency(perk.previousCost, { currencySymbol: "$" })}
              </p>
            ) : null}
            {perk.discount && perk.previousCost !== 0 ? (
              <p className="w-[49px] h-[20px] rounded-[5px] bg-[#F1E2FF] text-brand-500 text-[12px] font-[600] flex items-center justify-center">
                - {perk.discount}%
              </p>
            ) : null}
          </div>
          <p className="text-[16px] font-[600] text-brand-500">
            {perk.cost
              ? formatCurrency(perk.cost, { currencySymbol: "$" })
              : "Free"}
            <span className="font-[500] text-[#8A8A8A]">
              {perk.cost ? "/m" : ""}
            </span>
          </p>
        </div>
        {redirectOnPress ? (
          <Link href={redirectOnPress} passHref legacyBehavior>
            <a>{button}</a>
          </Link>
        ) : (
          button
        )}
      </div>
    </div>
  );
};
